'use client';

import { useEffect, useRef } from 'react';
import Editor, { OnMount, useMonaco } from '@monaco-editor/react';
import { FileCode } from 'lucide-react';
import { useBoardEditorSaveFile } from '@/hooks/feature/boards/useBoardEditorSaveFile';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { useModalSlideStore } from '@/stores/useModalSlideStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import BoardCodeFileList from '@/(routers)/boards/editor/components/modal/BoardCodeFileList';
import BoardBlockSyncBanner from '@/(routers)/boards/editor/components/unit/BoardBlockSyncBanner';

export default function BoardCodeEditor({ relayoutTick = 0 }: { relayoutTick?: number }) {
    const code = useBoardEditorStore((s) => s.code);
    const setCode = useBoardEditorStore((s) => s.setCode);
    const isDark = useBoardEditorConfigStore((s) => s.isDark);
    const editorMode = useBoardEditorStore((s) => s.editorMode);
    const { currentFileId, markAsDirty, updateFile, files } = useBoardEditorFileStore();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const editorRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);
    const monaco = useMonaco();
    const { openModalSlide, closeModalSlide } = useModalSlideStore();
    const theme = isDark ? 'vs-dark' : 'vs';
    const currentFile = files.find((f) => f.id === currentFileId);
    const isBlockSynced = currentFile?.isBlockSynced ?? true;

    const { save } = useBoardEditorSaveFile();
    const saveRef = useRef(save);
    useEffect(() => {
        saveRef.current = save;
    }, [save]);

    const handleRestoreConfirm = () => {
        const file = useBoardEditorFileStore.getState().getCurrentFile();
        if (file && currentFileId) {
            const blockContent = file.preEditBlockContent || file.blockContent;
            updateFile(currentFileId, {
                blockContent,
                isBlockSynced: true,
                isCodeEditConfirmed: false,
                preEditBlockContent: '',
            });
        }
        useBoardEditorStore.getState().setEditorMode('block');
        window.dispatchEvent(new CustomEvent('blockly-restore'));
    };

    const onMount: OnMount = (editor, monacoInstance) => {
        editorRef.current = editor;
        requestAnimationFrame(() => editor.layout());
        if (monacoInstance) monacoInstance.editor.setTheme('custom-vs');

        if (monacoInstance) {
            editor.addAction({
                id: 'format-document',
                label: 'Format Document',
                keybindings: [
                    monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyF,
                ],
                run: (ed) => {
                    ed.getAction('editor.action.formatDocument')?.run();
                },
            });

            editor.addAction({
                id: 'toggle-comment',
                label: 'Toggle Line Comment',
                keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Slash],
                run: (ed) => {
                    ed.getAction('editor.action.commentLine')?.run();
                },
            });

            editor.addAction({
                id: 'find',
                label: 'Find',
                keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyF],
                run: (ed) => {
                    ed.getAction('actions.find')?.run();
                },
            });

            editor.addAction({
                id: 'replace',
                label: 'Replace',
                keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyH],
                run: (ed) => {
                    ed.getAction('editor.action.startFindReplaceAction')?.run();
                },
            });

            const container = editor.getDomNode();
            if (container) {
                const observer = new MutationObserver(() => {
                    const widget = container.querySelector('.find-widget');
                    if (!widget) return;
                    const isVisible = (widget as HTMLElement).style.display !== 'none';
                    if (isVisible && widget.getAttribute('aria-hidden') === 'true') {
                        widget.setAttribute('aria-hidden', 'false');
                    }
                });
                observer.observe(container, { subtree: true, attributeFilter: ['aria-hidden'] });
                mutationObserverRef.current = observer;
            }
        }
    };

    useEffect(() => {
        return () => {
            mutationObserverRef.current?.disconnect();
            mutationObserverRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (editorMode !== 'code') return;
        requestAnimationFrame(() => {
            editorRef.current?.layout();
        });
    }, [editorMode]);

    useEffect(() => {
        if (!monaco) return;
        monaco.editor.defineTheme('custom-vs', {
            base: theme,
            inherit: true,
            rules: [],
            colors: {
                'editor.lineHighlightBackground': '#f5f5f5',
                'editor.lineHighlightBorder': '#00000000',
            },
        });
        monaco.editor.setTheme('custom-vs');
    }, [monaco, theme]);

    useEffect(() => {
        editorRef.current?.layout();
    }, [relayoutTick]);

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => editorRef.current?.layout());
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const handleCodeChange = (newCode: string | undefined) => {
        const updatedCode = newCode ?? '';
        setCode(updatedCode);
        if (currentFileId) {
            markAsDirty(currentFileId);
            const currentFile = useBoardEditorFileStore.getState().files.find((f) => f.id === currentFileId);
            const updates: Partial<import('@/stores/useBoardEditorFileStore').CodeFile> = {
                codeContent: updatedCode,
                isBlockSynced: false,
            };
            if (currentFile?.isBlockSynced) {
                updates.preEditBlockContent = currentFile.blockContent;
            }
            updateFile(currentFileId, updates);
        }
    };

    const handleOpenFileList = () => {
        openModalSlide({
            id: 'board-code-file-list',
            component: <BoardCodeFileList onClose={() => closeModalSlide()} />,
            size: { pcWidth: '900px', mbWidth: '100%', height: '100%' },
            direction: 'right',
        });
    };

    const emptyStateBgClass = isDark ? 'bg-[#1e1e1e]' : 'bg-white';
    const emptyStateTextClass = isDark ? 'text-gray-400' : 'text-color-co-gray-dark';
    const emptyStateIconClass = isDark ? 'text-gray-500' : 'text-color-co-gray-dark';

    return (
        <div ref={containerRef} className="absolute inset-0 min-w-0 overflow-hidden">
            {!isBlockSynced && <BoardBlockSyncBanner onConfirm={handleRestoreConfirm} />}
            {!currentFileId && isAuthenticated ? (
                <div className={`absolute inset-0 flex items-center justify-center ${emptyStateBgClass}`}>
                    <button
                        onClick={handleOpenFileList}
                        className="flex flex-col items-center gap-4 text-center hover:opacity-70 transition-opacity cursor-pointer"
                    >
                        <IconAdaptive Icon={FileCode} strokeWidth={1.5} baseSize={48} className={emptyStateIconClass} />
                        <div className={`font-corp text-sm ${emptyStateTextClass}`}>
                            コードファイルを選択してください
                        </div>
                    </button>
                </div>
            ) : (
                <div className="absolute inset-0" style={{ display: 'flex', flexDirection: 'column' }}>
                    <Editor
                        width="100%"
                        height="100%"
                        defaultLanguage="cpp"
                        value={code}
                        theme="custom-vs"
                        onMount={onMount}
                        onChange={handleCodeChange}
                        loading={
                            <div
                                className={`flex items-center justify-center w-full h-full ${
                                    isDark ? 'bg-[#1e1e1e]' : 'bg-white'
                                }`}
                            >
                                <ImageAdaptive
                                    src="/images/common/loading-circle.svg"
                                    baseWidth={60}
                                    breakpoints={{ md: 60 }}
                                />
                            </div>
                        }
                        options={{
                            minimap: { enabled: false },
                            fontSize: 14,
                            lineHeight: 22,
                            padding: { top: 12, bottom: 12 },
                            automaticLayout: false,
                            scrollBeyondLastLine: false,
                            tabSize: 2,
                            renderLineHighlight: 'none',
                            find: {
                                addExtraSpaceOnTop: false,
                                autoFindInSelection: 'never',
                                seedSearchStringFromSelection: 'never',
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}
